import {
  Project,
  Baseline,
  OneOfParentElements,
} from '@shared/models/project.model';
import { isBuildingVersionElement } from '../recursive-element.helpers';
import { ResultsRecord } from '@shared/models/unit.model';
import {
  applyBatchChanges,
  applyBatchChangesToParent,
  buildElementPathIndex,
  createEmptyBatchChangeMap,
  getElementByPath,
  pruneBatchChangeMapAfterElementRemovals,
  type ApplyBatchChangesOptions,
  type BatchChangeMap,
  type ElementChangeMap,
  type ElementRemoveChange,
  type ScopedMutatorBucket,
} from './batch-update.helpers';
import { isOneOf } from '../array.helpers';
import {
  ProjectChange,
  ProjectChangeType,
  VersionChange,
  NonVersionChange,
  ElementChange,
  nonVersionChangeTypes,
  versionChangeTypes,
} from './update-engine.models';
import {
  cleanupActionsForRemovedElements,
  collectRemovedElementIds,
} from '../actions/clean-actions.helpers';
import { removeElements } from '../project.helpers';
import {
  applyElementAddChangeToVersion,
  applyVersionMutateChange,
  versionMutateChangeTypes,
  type ElementAddChange,
  type VersionAddChange,
  type VersionMutateChange,
  type VersionRemoveChange,
} from './mutators';
import { versionChangeToPartial } from './updates/version-change.update';

export type { ApplyBatchChangesOptions } from './batch-update.helpers';
export type { ProjectChange } from './update-engine.models';
export { ProjectChangeType } from './update-engine.models';
import { hasPartialChanges } from '../object.helpers';
import { guards } from '../typeguards.helpers';
import {
  projectChangeToElementPartial,
  mergeElementPartials,
  getProjectChangeKind,
  getProjectChangeScope,
} from './updates';

/**
 * Resolves the batch grouping key for a {@link ProjectChange}.
 * Element-scoped changes use `elementId`; version-only changes use `versionId`.
 * When both exist (e.g. proposal select/deselect), `elementId` is used so changes batch per element.
 *
 * @param change - Change payload
 * @returns Valid id string when a known field is present; otherwise `''` (no path match downstream)
 */
const getChangeGroupKey = (change: ProjectChange): string => {
  if ('elementId' in change) {
    const { elementId } = change;
    if (guards.is.string(elementId)) return elementId;
  }
  if ('versionId' in change) {
    const { versionId } = change;
    if (guards.is.string(versionId)) return versionId;
  }
  // Malformed or unknown shape — avoid coercing bad ids into one bucket with real elements/versions
  return '';
};

/** Append `value` to the array stored under `key` in `target`, creating the array on first use. */
const pushToMapBucket = <K, V>(target: Map<K, V[]>, key: K, value: V): void => {
  let bucket = target.get(key);
  if (!bucket) {
    bucket = [];
    target.set(key, bucket);
  }
  bucket.push(value);
};

/** Pick the right `add | remove | update` map inside a {@link ScopedMutatorBucket}. */
const getMutatorKindBucket = (
  bucket: ScopedMutatorBucket,
  kind: 'add' | 'remove' | 'update',
): Map<string, VersionMutateChange[]> => {
  if (kind === 'add') return bucket.add;
  if (kind === 'remove') return bucket.remove;
  return bucket.update;
};

/**
 * Categorize project changes into the unified {@link BatchChangeMap} consumed by
 * {@link applyBatchChanges}.
 *
 * Each change is routed by its declared `(scope, kind)` (see
 * `pipeline/updates/index.ts`):
 * - `scope: 'element'` + `'add' | 'remove'` → `element.add` / `element.remove` buckets,
 *   applied via the project-tree structural mutators.
 * - `scope: 'element'` + `'update'` → `element.update`, grouped by element id so per-element
 *   partials merge in batch.
 * - `scope: 'version'` + `'add' | 'remove'` → `version.add` / `version.remove`, applied via
 *   the project-level version mutators (versions live on the building, not inside another
 *   version).
 * - `scope: 'version'` + `'update'` → `version.update`, grouped by version id; the apply
 *   pipeline distinguishes primitive partials from structural resets at apply time.
 * - `scope: 'proposal' | 'action'` (any kind) → `proposal.{kind}` / `action.{kind}` per
 *   `versionId`, applied as ordered mutator chains in {@link applyBatchChanges}.
 *
 * Resolution into element/version partials is intentionally deferred to
 * {@link applyBatchChanges} so update handlers see the post-structural project state.
 *
 * @param changes - Changes to categorize
 * @returns Structured map ready for {@link applyBatchChanges}
 */
const buildElementChangesMap = (changes: ProjectChange[]): BatchChangeMap => {
  const map = createEmptyBatchChangeMap();

  for (const change of changes) {
    const scope = getProjectChangeScope(change.type);
    const kind = getProjectChangeKind(change.type);

    // Element scope ─────────────────────────────────────────────────────────
    if (scope === 'element') {
      if (kind === 'remove') {
        map.element.remove.push(change as ElementRemoveChange);
        continue;
      }
      if (kind === 'add') {
        map.element.add.push(change as ElementAddChange);
        continue;
      }
      // kind === 'update'
      const id = getChangeGroupKey(change);
      if (!id) continue;
      pushToMapBucket(map.element.update, id, change);
      continue;
    }

    // Version scope ─────────────────────────────────────────────────────────
    if (scope === 'version' && kind === 'add') {
      map.version.add.push(change as VersionAddChange);
      continue;
    }

    if (scope === 'version' && kind === 'remove') {
      map.version.remove.push(change as VersionRemoveChange);
      continue;
    }

    // Version scope (primitive partials + bulk resets like ClearProposals) ─
    if (scope === 'version' && kind === 'update') {
      const id = getChangeGroupKey(change);
      if (!id) continue;
      pushToMapBucket(map.version.update, id, change);
      continue;
    }

    // Proposal / Action scope ───────────────────────────────────────────────
    // Both v1 and changes carry `versionId` (see ProjectChangeMap version-change payloads),
    // so we can safely narrow to VersionMutateChange and key by version.
    if ((scope === 'proposal' || scope === 'action') && kind != null) {
      const mutateChange = change as VersionMutateChange;
      const versionId = mutateChange.versionId;
      if (!versionId) continue;
      const scopeBucket = scope === 'proposal' ? map.proposal : map.action;
      pushToMapBucket(
        getMutatorKindBucket(scopeBucket, kind),
        versionId,
        mutateChange,
      );
      continue;
    }

    // Unregistered or future scopes — fall back to update bucket keyed by best-effort id.
    const id = getChangeGroupKey(change);
    if (!id) continue;
    pushToMapBucket(map.element.update, id, change);
  }

  return map;
};

/**
 * Apply multiple project changes to a project via the unified pipeline.
 *
 * Categorizes the changes into a {@link BatchChangeMap} and delegates to
 * {@link applyBatchChanges}, which guarantees structural changes are processed before
 * updates (see ordering contract on {@link BatchChangeMap}).
 *
 * @param project - The project to update
 * @param changes - Changes to apply
 * @param options - Forwarded to {@link applyBatchChanges}. Pass
 *   `{ replayOfRace: true }` when rebasing onto server truth after a 422.
 * @returns Updated project (same reference if no changes)
 */
export const applyProjectChanges = (
  project: Project,
  changes: ProjectChange[],
  options?: ApplyBatchChangesOptions,
): Project => {
  if (changes.length === 0) return project;
  return applyBatchChanges(project, buildElementChangesMap(changes), options);
};

/**
 * Applies structural element removals from {@link BatchChangeMap} to a parent root.
 *
 * @param working - Current parent root
 * @param map - Batched changes, pruned in-place after removals
 * @returns Updated parent root
 */
const applyElementRemoveChangesToParent = <T extends OneOfParentElements>(
  working: T,
  map: BatchChangeMap,
): T => {
  if (map.element.remove.length === 0) return working;
  if (!isBuildingVersionElement(working)) {
    throw new Error(
      'applyProjectChangesToParent: ElementRemove requires a building version root',
    );
  }

  const removalIds = map.element.remove.map((c) => c.elementId);
  // Capture descendants before mutation so queued changes/actions can be scrubbed.
  const removedIds = collectRemovedElementIds(working, removalIds);
  const versionIdForPrune = working.id;
  let next: Baseline = removeElements(working, ...removalIds);

  if (removedIds.size > 0) {
    pruneBatchChangeMapAfterElementRemovals(
      map,
      new Map([[versionIdForPrune, removedIds]]),
    );
    next = cleanupActionsForRemovedElements(next, removedIds);
  }

  return next as T;
};

/**
 * Applies structural element additions from {@link BatchChangeMap} to a parent root.
 *
 * @param working - Current parent root
 * @param map - Batched changes containing element add operations
 * @returns Updated parent root
 */
const applyElementAddChangesToParent = <T extends OneOfParentElements>(
  working: T,
  map: BatchChangeMap,
): T => {
  if (map.element.add.length === 0) return working;
  if (!isBuildingVersionElement(working)) {
    throw new Error(
      'applyProjectChangesToParent: ElementAdd requires a building version root',
    );
  }

  let workingVersion: Baseline = working;
  for (const change of map.element.add) {
    workingVersion = applyElementAddChangeToVersion(workingVersion, change);
  }

  return workingVersion as T;
};

/**
 * Apply non-version project changes to a parent (version or element).
 *
 * Mirrors {@link applyProjectChanges} for a single parent root. Application order matches
 * {@link applyBatchChanges}: removes → adds → version mutators → update partials. Structural
 * and mutate steps require a building-version root and throw otherwise.
 *
 * @param parent - The root node (version or element) to update
 * @param changes - Changes to apply
 * @returns Updated parent (same reference if no changes)
 */
export const applyProjectChangesToParent = <T extends OneOfParentElements>(
  parent: T,
  changes: ProjectChange[],
): T => {
  if (changes.length === 0) return parent;

  const map = buildElementChangesMap(changes);

  if (map.version.add.length > 0 || map.version.remove.length > 0) {
    throw new Error(
      'applyProjectChangesToParent: VersionAdd/VersionRemove requires a project root',
    );
  }

  let working = parent;

  working = applyElementRemoveChangesToParent(working, map);
  working = applyElementAddChangesToParent(working, map);

  // Per-version structural mutators (proposals + actions) target the parent version itself;
  // only meaningful when parent is a building version AND the change's versionId matches.
  const hasVersionMutators =
    map.proposal.add.size > 0 ||
    map.proposal.remove.size > 0 ||
    map.proposal.update.size > 0 ||
    map.action.add.size > 0 ||
    map.action.remove.size > 0 ||
    map.action.update.size > 0;

  if (hasVersionMutators) {
    if (!isBuildingVersionElement(working)) {
      throw new Error(
        'applyProjectChangesToParent: proposal/action changes require a building version root',
      );
    }
    const ordered = collectVersionMutatorsForParent(map, working.id);
    if (ordered.length > 0) {
      let workingVersion: Baseline = working;
      for (const change of ordered) {
        workingVersion = applyVersionMutateChange(workingVersion, change);
      }
      working = workingVersion as T;
    }
  }

  // Version-scope updates (primitive partials + structural resets) on the parent version.
  if (map.version.update.size > 0) {
    if (!isBuildingVersionElement(working)) {
      throw new Error(
        'applyProjectChangesToParent: version-scope changes require a building version root',
      );
    }
    const versionChanges = map.version.update.get(working.id);
    if (versionChanges?.length) {
      working = applyVersionUpdatesToVersion(working, versionChanges) as T;
    }
  }

  if (map.element.update.size === 0) return working;

  const partials = resolveUpdatePartialsForParent(working, map.element.update);
  if (partials.size === 0) return working;

  // Must allow `elements` on partials (e.g. {@link ProjectChangeType.ElementCategoryEnrich});
  // default {@link applyBatchChangesToParent} would strip them and leave product slots stale.
  return applyBatchChangesToParent(working, partials, false);
};

/**
 * Flatten proposal+action mutators for one version in the same fixed sub-order used by
 * {@link applyBatchChanges}: proposal.remove → action.remove → proposal.add → action.add →
 * proposal.update → action.update.
 */
const collectVersionMutatorsForParent = (
  map: BatchChangeMap,
  versionId: string,
): VersionMutateChange[] => {
  const ordered: VersionMutateChange[] = [];
  const push = (source: Map<string, VersionMutateChange[]>) => {
    const list = source.get(versionId);
    if (list?.length) ordered.push(...list);
  };
  push(map.proposal.remove);
  push(map.action.remove);
  push(map.proposal.add);
  push(map.action.add);
  push(map.proposal.update);
  push(map.action.update);
  return ordered;
};

/**
 * Apply a single version's `version.update` chain (mix of primitive partials and
 * structural resets like `VersionClearProposals`) to one {@link Baseline}.
 */
const applyVersionUpdatesToVersion = (
  version: Baseline,
  changes: ProjectChange[],
): Baseline => {
  let next: Baseline = version;
  for (const change of changes) {
    if (isVersionMutateChangeType(change.type)) {
      next = applyVersionMutateChange(next, change as VersionMutateChange);
      continue;
    }
    if (!isVersionChange(change)) continue;
    const partial = versionChangeToPartial(next, change);
    if (partial) next = { ...next, ...partial } as Baseline;
  }
  return next;
};

const versionMutateChangeTypeSetForParent = new Set<ProjectChangeType>(
  versionMutateChangeTypes,
);

const isVersionMutateChangeType = (type: ProjectChangeType): boolean =>
  versionMutateChangeTypeSetForParent.has(type);

/**
 * Resolve raw update changes into a single merged partial per element id, scoped to a
 * single parent (typically a building version). Structural changes are not handled here.
 *
 * Mirrors the resolver inside {@link applyBatchChanges} but operates on a parent path
 * index, and only resolves element-level changes (version changes are silently ignored
 * here — they belong on the project-level path).
 */
const resolveUpdatePartialsForParent = (
  parent: OneOfParentElements,
  updateChanges: Map<string, ProjectChange[]>,
): ElementChangeMap => {
  const pathIndex = buildElementPathIndex(parent);
  const partials: ElementChangeMap = new Map();

  for (const [id, changes] of updateChanges) {
    const path = pathIndex.get(id);
    if (!path) continue;

    const elementLevel = changes.filter(isNonVersionChange);
    if (elementLevel.length === 0) continue;

    const element = getElementByPath(parent, path);
    if (!element) continue;

    let merged: Partial<typeof element> = {};
    let currentElement = element;

    for (const change of elementLevel) {
      const partial = projectChangeToElementPartial(currentElement, change);
      if (partial) {
        merged = mergeElementPartials(merged, partial);
        currentElement = { ...currentElement, ...partial } as typeof element;
      }
    }

    if (Object.keys(merged).length > 0 && hasPartialChanges(element, merged)) {
      partials.set(id, merged);
    }
  }

  return partials;
};

/**
 * Apply element-only changes to a parent (version or element).
 * Accepts only {@link ElementChange} — no product-element or version changes.
 * Narrower than {@link applyProjectChangesToParent} for call-sites that only deal with element mutations.
 *
 * @param parent - The root node (version or element) to update
 * @param changes - Element-scoped changes to apply
 * @returns Updated parent (same reference if no changes)
 */
export const applyElementChanges = <T extends OneOfParentElements>(
  parent: T,
  changes: ElementChange[],
): T => applyProjectChangesToParent(parent, changes);

/**
 * Create a change for element property resolved value
 */
export const createPropertyResolvedChange = (
  elementId: string,
  propertyName: string,
  value: number,
): ProjectChange => ({
  type: ProjectChangeType.ElementPropertyResolved,
  elementId,
  propertyName,
  value,
});

/**
 * Rename a non-version element (display name + fallback).
 */
export const createElementNameChange = (
  elementId: string,
  name: string,
  fallbackName: string,
): ProjectChange => ({
  type: ProjectChangeType.ElementName,
  elementId,
  name,
  fallbackName,
});

/**
 * Remove an element or building version by id (same semantics as {@link removeElements}).
 */
export const createElementRemoveChange = (
  elementId: string,
): ProjectChange => ({
  type: ProjectChangeType.ElementRemove,
  elementId,
});

/**
 * Create a change for product element count (resolved)
 */
export const createProductCountResolvedChange = (
  elementId: string,
  value: number,
): ProjectChange => ({
  type: ProjectChangeType.ProductElementCountResolved,
  elementId,
  value,
});

/**
 * Create a change setting `resultsRecord` on a {@link IProposal} in
 * {@link Baseline.proposals}.
 */
export const createProposalResultsChange = (
  versionId: string,
  proposalId: string,
  resultsRecord: ResultsRecord,
): ProjectChange => ({
  type: ProjectChangeType.ProposalResultsRecord,
  versionId,
  proposalId,
  resultsRecord,
});

/**
 * Type guard for version-scoped {@link ProjectChange}s.
 * Inverse of {@link isNonVersionChange}; both read {@link versionChangeTypes}.
 */
export const isVersionChange = (
  change: ProjectChange,
): change is VersionChange => isOneOf(versionChangeTypes, change.type);

/** Type guard for any non-version change (element or product element) */
export const isNonVersionChange = (
  change: ProjectChange,
): change is NonVersionChange => {
  return isOneOf(nonVersionChangeTypes, change.type);
};
